import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { BookOpen, Target, MessageSquare, Users, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENT_ICONS = {
  qualify_sales_lead: Target,
  gather_feedback: MessageSquare,
  screen_candidate: Users,
};

const INTENT_COLORS = {
  qualify_sales_lead: 'text-red-600 bg-red-50 border-red-200',
  gather_feedback: 'text-emerald-600 bg-emerald-50 border-emerald-200',
  screen_candidate: 'text-blue-600 bg-blue-50 border-blue-200',
};

export const PlaybookSelector = ({ qrId, currentPlaybookId, onUpdate }) => {
  const { token } = useAuth();
  const [playbooks, setPlaybooks] = useState([]);
  const [selected, setSelected] = useState(currentPlaybookId || null);
  const [saving, setSaving] = useState(false);

  const fetchPlaybooks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/playbooks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlaybooks(res.data);
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchPlaybooks();
  }, [fetchPlaybooks]);

  useEffect(() => {
    setSelected(currentPlaybookId || null);
  }, [currentPlaybookId]);


  const handleSelect = async (pbId) => {
    const newId = pbId === selected ? null : pbId;
    setSaving(true);
    try {
      if (newId) {
        await axios.patch(`${API}/qr-codes/${qrId}/playbook`,
          { playbook_id: newId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Playbook assigned — interview mode set to Adaptive');
      } else {
        await axios.delete(`${API}/qr-codes/${qrId}/playbook`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Playbook removed');
      }
      setSelected(newId);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update playbook');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="playbook-selector">
      <div className="flex items-center gap-2 mb-2">
        <BookOpen className="h-5 w-5 text-[#1d4ed8]" />
        <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
          AI Interview Playbook
        </h3>
      </div>
      <p className="text-xs text-[hsl(var(--muted-foreground))] -mt-2">
        Select a playbook to define how the AI conducts this interview.
      </p>

      <div className="space-y-3">
        {playbooks.map(pb => {
          const Icon = INTENT_ICONS[pb.intent] || BookOpen;
          const colors = INTENT_COLORS[pb.intent] || 'text-[#475569] bg-[#F1F5F9] border-[#E2E8F0]';
          const isActive = selected === pb.id;

          return (
            <Card
              key={pb.id}
              className={`p-4 cursor-pointer transition-all border-2 ${
                isActive
                  ? 'border-[#1d4ed8] bg-[#EFF6FF]'
                  : 'border-[hsl(var(--border))] hover:border-[#93C5FD]'
              }`}
              onClick={() => !saving && handleSelect(pb.id)}
              data-testid={`playbook-card-${pb.id}`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && !saving && handleSelect(pb.id)}
              aria-pressed={isActive}
              aria-label={`${pb.name} playbook${isActive ? ' (selected)' : ''}`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${colors} border`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-bold text-sm">{pb.name}</h4>
                    {isActive && <Check className="h-4 w-4 text-[#1d4ed8]" />}
                  </div>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{pb.description}</p>

                  {/* Extraction fields preview */}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(pb.extraction_fields || []).map((f, i) => (
                      <span
                        key={`item-${i}`}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] font-medium"
                      >
                        {f.label || f.name}
                        {f.required && <span className="text-red-400 ml-0.5">*</span>}
                      </span>
                    ))}
                  </div>

                  {/* Meta info */}
                  <div className="flex gap-3 mt-2 text-[10px] text-[hsl(var(--muted-foreground))]">
                    <span>Depth: {pb.probing_depth}</span>
                    <span>{pb.extraction_fields?.length || 0} fields</span>
                    <span>{pb.lead_scoring_rules?.length || 0} scoring rules</span>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {selected && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleSelect(null)}
          disabled={saving}
          className="rounded-full text-xs"
          data-testid="remove-playbook-btn"
        >
          Remove Playbook
        </Button>
      )}
    </div>
  );
};
